import React, { FC } from 'react';
import { MdCheckCircleOutline, MdErrorOutline, MdInfoOutline, MdWarningAmber } from 'react-icons/md';
import clsx from 'clsx';
import { sanitizeMessageHtml } from '../../lib/html';
import './MessageTone.scss';

/** Тон окна сообщения: иконка и её цвет в шапке. `danger` — вопрос перед необратимым действием. */
export type MessageTone = 'error' | 'warning' | 'info' | 'success' | 'danger';

const ICONS: Record<MessageTone, React.ReactNode> = {
    error: <MdErrorOutline size={20} />,
    danger: <MdWarningAmber size={20} />,
    warning: <MdWarningAmber size={20} />,
    info: <MdInfoOutline size={20} />,
    success: <MdCheckCircleOutline size={20} />,
};

export interface IMessageToneHeaderProps {
    tone: MessageTone;
    title: React.ReactNode;
    /** id заголовка — им окно подписано (`aria-labelledby` у `Modal`) */
    id: string;
}

/** Шапка окна сообщения: плашка с иконкой тона и заголовок. Тело окна под ней — с классом `message-tone__body`,
 *  чтобы текст начинался по заголовку. */
export const MessageToneHeader: FC<IMessageToneHeaderProps> = ({ tone, title, id }) => (
    <div className="message-tone__head">
        <span className={clsx('message-tone__icon', `message-tone__icon--${tone}`)} aria-hidden="true">
            {ICONS[tone]}
        </span>
        <h2 id={id} className="rf-modal__title message-tone__title">
            {title}
        </h2>
    </div>
);

export interface IMessageToneListProps {
    /** Сообщения — HTML, очищается DOMPurify */
    messages: readonly string[];
    /** Префикс id сообщений: `${idPrefix}-${index}` — для `aria-describedby` окна */
    idPrefix: string;
}

const MessageText: FC<{ id: string; html: string }> = ({ id, html }) => (
    <div id={id} className="message-tone__text" dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(html) }} />
);

/** Текст окна: одно сообщение — абзацем, несколько — пронумерованным списком в рамке. */
export const MessageToneList: FC<IMessageToneListProps> = ({ messages, idPrefix }) => {
    if (messages.length === 0) return null;
    if (messages.length === 1) return <MessageText id={`${idPrefix}-0`} html={messages[0]} />;
    return (
        <ol className="message-tone__list">
            {messages.map((message, index) => (
                <li key={index} className="message-tone__item">
                    <span className="message-tone__num" aria-hidden="true">
                        {index + 1}
                    </span>
                    <MessageText id={`${idPrefix}-${index}`} html={message} />
                </li>
            ))}
        </ol>
    );
};
